import React, { useState, useEffect } from 'react';
import { UserSettings } from '../../types/index';

interface WhatsAppConfigCardProps {
  preferences: UserSettings;
  webhookUrl: string;
  verifyToken: string;
  tokenSaveNonce?: number;
  onCopy: (text: string, message: string) => Promise<string>;
  onUpdate?: (preferences: UserSettings) => void;
}

const WhatsAppTooltip: React.FC = () => (
  <div
    className="absolute z-50 w-96 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-sm text-gray-600"
    style={{ bottom: '100%', right: '-200px', marginBottom: '10px' }}
  >
    <p className="mb-2">
      Sigue estos pasos para obtener tu ID de teléfono y ID de negocio de WhatsApp desde Meta:
    </p>
    <ol className="list-decimal list-inside space-y-2 mb-4">
      <li>
        Inicia sesión en el{' '}
        <a
          href="https://developers.facebook.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-secondary hover:underline"
        >
          sitio de desarrolladores de Meta
        </a>
        .
      </li>
      <li>Haz clic en la pestaña "My apps".</li>
      <li>
        Navega a "API Setup" en la sección de WhatsApp para obtener el número de teléfono y el ID de
        negocio de WhatsApp.
      </li>
    </ol>
    <img
      src="/wa_business_id.png"
      alt="WhatsApp Business ID Location"
      className="rounded-lg w-full h-auto"
    />
  </div>
);

const WhatsAppConfigCard: React.FC<WhatsAppConfigCardProps> = ({
  preferences,
  webhookUrl,
  verifyToken,
  tokenSaveNonce = 0,
  onCopy,
  onUpdate,
}) => {
  const [newWaAccessToken, setNewWaAccessToken] = useState('');

  useEffect(() => {
    if (tokenSaveNonce > 0) {
      setNewWaAccessToken('');
    }
  }, [tokenSaveNonce]);

  const handleChange = (field: string, value: any) => {
    if (onUpdate) {
      onUpdate({
        ...preferences,
        [field]: value,
      });
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-primary">Configuración de WhatsApp</h2>

        {/* Assistant Enabled Toggle */}
        <div className="flex items-center">
          <span className="text-md text-primary mr-4">Habilitar Asistente:</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={preferences.assistantEnabled}
              onChange={(e) => handleChange('assistantEnabled', e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
          </label>
        </div>
      </div>

      <p className="text-gray-600 mb-4">
        Configura tu integración con WhatsApp Business API para permitir que tus clientes se
        comuniquen contigo a través de WhatsApp.
      </p>

      {/* Alert Warning Banner */}
      {!preferences.waPhoneNumberId ||
      !preferences.waBusinessAccountId ||
      !preferences.waConnected ? (
        <div className="p-4 mb-4 text-yellow-700 bg-yellow-100 border border-yellow-300 rounded">
          <p className="font-semibold">¡Atención!</p>
          <p>
            Por favor, completa todos los campos de configuración de WhatsApp. Puedes seguir nuestra
            guía{' '}
            <a
              href="https://asistentefacil.com/whatsapp-guide.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondary hover:underline"
            >
              aquí
            </a>
            .
          </p>
        </div>
      ) : (
        <p className="text-gray-600 mb-4">
          Para crear y configurar una cuenta de Whatsapp Business y obtener el token, puedes seguir
          nuestra guía{' '}
          <a
            href="https://asistentefacil.com/whatsapp-guide.html"
            target="_blank"
            rel="noopener noreferrer"
            className="text-secondary hover:underline"
          >
            aquí
          </a>
          .
        </p>
      )}

      {/* Webhook URL Information */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-md font-semibold text-primary mb-2">Configuración del Webhook</h3>
        <p className="text-xs text-gray-600 mb-3">
          Utiliza la siguiente información para configurar el webhook en tu panel de WhatsApp
          Business:
        </p>

        <div className="mb-3">
          <label className="block text-primary text-xs font-bold mb-1">URL del Webhook</label>
          <div className="flex">
            <input
              type="text"
              value={webhookUrl}
              readOnly
              className="flex-grow px-3 py-2 bg-white border border-gray-300 rounded-l-lg focus:outline-none text-sm"
            />
            <button
              onClick={() => onCopy(webhookUrl, 'URL del webhook copiada al portapapeles')}
              className="px-3 py-2 bg-secondary text-white rounded-r-lg hover:bg-secondary/90"
              title="Copiar al portapapeles"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                />
              </svg>
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Esta es la URL única para tu webhook de WhatsApp.
          </p>
        </div>

        <div>
          <label className="block text-primary text-xs font-bold mb-1">Token de Verificación</label>
          <div className="flex">
            <input
              type="text"
              value={verifyToken}
              readOnly
              className="flex-grow px-3 py-2 bg-white border border-gray-300 rounded-l-lg focus:outline-none text-sm"
            />
            <button
              onClick={() => onCopy(verifyToken, 'Token de verificación copiado al portapapeles')}
              className="px-3 py-2 bg-secondary text-white rounded-r-lg hover:bg-secondary/90"
              title="Copiar al portapapeles"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                />
              </svg>
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Utiliza este token para verificar tu webhook en WhatsApp Business.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <label className="block text-primary text-sm font-bold mb-2 flex items-center">
            ID del Número de Teléfono
            <div className="relative ml-2 cursor-help">
              <div className="group">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="hidden group-hover:block absolute">
                  <WhatsAppTooltip />
                </div>
              </div>
            </div>
          </label>
          <input
            type="text"
            value={preferences.waPhoneNumberId || ''}
            onChange={(e) => handleChange('waPhoneNumberId', e.target.value)}
            placeholder="Ej: 123456789012345"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-gray-500 mt-1">
            El ID del número de teléfono de WhatsApp Business que recibirá los mensajes.
          </p>
        </div>

        <div className="relative">
          <label className="block text-primary text-sm font-bold mb-2 flex items-center">
            ID de la Cuenta de Negocio
            <div className="relative ml-2 cursor-help">
              <div className="group">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="hidden group-hover:block absolute">
                  <WhatsAppTooltip />
                </div>
              </div>
            </div>
          </label>
          <input
            type="text"
            value={preferences.waBusinessAccountId || ''}
            onChange={(e) => handleChange('waBusinessAccountId', e.target.value)}
            placeholder="Ej: 123456789012345"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-gray-500 mt-1">
            El ID de tu cuenta de negocio de WhatsApp Business.
          </p>
        </div>

        <div>
          <label className="block text-primary text-sm font-bold mb-2">Token de Acceso</label>
          <input
            type="password"
            value={newWaAccessToken}
            onChange={(e) => {
              setNewWaAccessToken(e.target.value);
              if (onUpdate) {
                onUpdate({
                  ...preferences,
                  waAccessToken: e.target.value || undefined,
                });
              }
            }}
            placeholder={
              preferences.waConnected
                ? 'Token configurado — deja en blanco para mantenerlo'
                : 'Token de acceso permanente'
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="text-xs text-gray-500 mt-1">
            {preferences.waConnected
              ? 'El token se guarda de forma segura en el servidor. Ingresa un valor solo para reemplazarlo.'
              : 'El token de acceso permanente para la API de WhatsApp Business.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppConfigCard;
